(() => {
  if (customElements.get('cs-highlights')) return;
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = () => matchMedia('(max-width: 749px)').matches;
  const hintTargets = new WeakSet();
  let firstHintAt = 0;

  function hintWhenVisible(track, delayAfterFirst, owner) {
    if (!track || hintTargets.has(track) || !mobile() || reducedMotion()) return;
    hintTargets.add(track);
    let timer = 0;
    let frame = 0;
    let touched = false;
    let finish = () => {};
    const cancel = () => {
      touched = true;
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      finish();
      track.dataset.swipeHint = 'cancelled';
    };
    ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(event =>
      track.addEventListener(event, cancel, { once: true, passive: true, signal: owner.signal })
    );
    owner.signal.addEventListener('abort', cancel, { once: true });
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      const delay = delayAfterFirst && firstHintAt ? Math.max(0, firstHintAt + 2000 - Date.now()) : 0;
      timer = setTimeout(() => {
        if (touched || !track.isConnected || !mobile() || reducedMotion() ||
            track.scrollWidth <= track.clientWidth + 8 || track.scrollLeft > 2) return;
        const oldSnap = track.style.scrollSnapType;
        const oldBehavior = track.style.scrollBehavior;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        finish = () => {
          track.style.scrollSnapType = oldSnap;
          track.style.scrollBehavior = oldBehavior;
        };
        const distance = Math.min(42, track.scrollWidth - track.clientWidth);
        const start = performance.now();
        if (!delayAfterFirst) firstHintAt = Date.now();
        track.dataset.swipeHint = 'running';
        const tick = now => {
          if (touched || !track.isConnected) return;
          const progress = Math.min(1, (now - start) / 760);
          track.scrollLeft = distance * Math.pow(Math.sin(Math.PI * progress), 2);
          if (progress < 1) frame = requestAnimationFrame(tick);
          else {
            track.scrollLeft = 0;
            finish();
            track.dataset.swipeHint = 'done';
          }
        };
        frame = requestAnimationFrame(tick);
      }, delay);
    }, { threshold: .45 });
    observer.observe(track);
    owner.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  customElements.define('cs-highlights', class extends HTMLElement {
    connectedCallback() {
      if (this.owner) return;
      this.owner = new AbortController();
      const options = { signal: this.owner.signal };
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      const activate = (tab, focus = false) => {
        const index = this.tabs.indexOf(tab);
        this.tabs.forEach((item, n) => {
          item.setAttribute('aria-selected', String(n === index));
          item.tabIndex = n === index ? 0 : -1;
          this.panels[n].hidden = n !== index;
        });
        if (focus) tab.focus({ preventScroll: true });
        if (mobile()) {
          const strip = tab.parentElement;
          const rect = tab.getBoundingClientRect();
          const outer = strip.getBoundingClientRect();
          if (rect.left < outer.left + 12 || rect.right > outer.right - 12) {
            strip.scrollTo({ left: strip.scrollLeft + rect.left - outer.left - 15, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        }
        this.updatePanel(this.panels[index]);
      };
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); activate(this.tabs[next], true); }
        }, options);
      });
      this.panels.forEach(panel => {
        const track = panel.querySelector('.cs-highlights__track');
        const cards = [...track.querySelectorAll('.cs-highlights__card')];
        const dots = [...panel.querySelectorAll('[data-slide]')];
        let scheduled = false;
        track.addEventListener('scroll', () => {
          if (scheduled) return;
          scheduled = true;
          requestAnimationFrame(() => { scheduled = false; if (this.isConnected) this.updatePanel(panel); });
        }, { ...options, passive: true });
        dots.forEach((dot, index) => dot.addEventListener('click', () => {
          const card = cards[index];
          track.scrollTo({ left: track.scrollLeft + card.getBoundingClientRect().left - track.getBoundingClientRect().left - parseFloat(getComputedStyle(track).paddingLeft), behavior: reducedMotion() ? 'auto' : 'smooth' });
        }, options));
        track.addEventListener('keydown', event => {
          if (event.target !== track || !mobile() || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = dots.findIndex(dot => dot.getAttribute('aria-pressed') === 'true');
          let next = Math.max(0, Math.min(cards.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = cards.length - 1;
          dots[next]?.click();
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        const tab = this.tabs.find(tab => tab.contains(event.target) || tab === event.target);
        if (tab) activate(tab);
      }, options);
      window.addEventListener('resize', () => this.panels.forEach(panel => this.updatePanel(panel)), options);
      this.panels.forEach(panel => this.updatePanel(panel));
      if (this.dataset.swipeHints === 'true') {
        const firstTrack = document.querySelector('.multicolumn-list__item--event-group')?.closest('ul');
        hintWhenVisible(firstTrack, false, this.owner);
        hintWhenVisible(this.panels[0]?.querySelector('.cs-highlights__track'), true, this.owner);
      }
    }
    updatePanel(panel) {
      if (!panel || panel.hidden) return;
      const track = panel.querySelector('.cs-highlights__track');
      const cards = [...track.querySelectorAll('.cs-highlights__card')];
      const left = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingLeft);
      let active = 0, distance = Infinity;
      cards.forEach((card, index) => {
        const delta = Math.abs(card.getBoundingClientRect().left - left);
        if (delta < distance) { distance = delta; active = index; }
      });
      panel.querySelectorAll('[data-slide]').forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === active)));
    }
    disconnectedCallback() { this.owner?.abort(); this.owner = null; }
  });
})();
